import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";
import { unique, formatTimeBucket, aggregateByCategory } from "./helpers";

/** bar + stacked_bar + horizontal_bar — they share the bulk of the setup. */
export function buildBarOption({ viz, query, data, palette, base, measure, dimension, xKey, yKey }: ChartBuildCtx): ChartOption {
  const horizontal = viz.type === "horizontal_bar";
  const yFmt = formatFromColumn(measure, data.columns);

  // Special shape: "1 measure × 1 dim × time" stacked bar.
  // Catalogue case `stacked-bar-channel-mix-by-month` uses this
  // pattern (measures=[cvs_sent], dimensions=[channel], time=month).
  // The natural read is "per month, stack channels by colour" — not
  // "one bar per channel summed across all months", which is what
  // the default dim-as-category path produces.
  const stackedTimeDim =
    viz.type === "stacked_bar" &&
    query.timeDimension &&
    (query.dimensions?.length ?? 0) === 1 &&
    query.measures.length === 1;
  if (stackedTimeDim) {
    const timeKey = query.timeDimension!.member;
    const groupKey = query.dimensions![0];
    const m = query.measures[0];
    const timeBuckets = unique(data.rows.map((r) => String(r[timeKey] ?? "")));
    const groups = unique(data.rows.map((r) => String(r[groupKey] ?? "")));
    const stackTooltipFmt = yFmt;
    const seriesByGroup = groups.map((g, gi) => {
      const dataByBucket = new Map<string, number>();
      for (const r of data.rows) {
        if (String(r[groupKey]) !== g) continue;
        const t = String(r[timeKey]);
        dataByBucket.set(t, (dataByBucket.get(t) ?? 0) + Number(r[m] ?? 0));
      }
      return {
        name: g,
        type: "bar" as const,
        stack: "total",
        color: palette[gi % palette.length],
        itemStyle: { borderRadius: gi === groups.length - 1 ? [8, 8, 0, 0] : [0, 0, 0, 0] },
        data: timeBuckets.map((b) => dataByBucket.get(b) ?? 0),
      };
    });
    return {
      ...base,
      tooltip: { trigger: "axis", valueFormatter: (v: unknown) => formatValue(v, stackTooltipFmt) },
      legend: {
        show: true,
        bottom: 0,
        textStyle: { color: "#6b7280", fontSize: 11 },
        formatter: (n: string) => (n.length > 22 ? n.slice(0, 21) + "…" : n),
      },
      xAxis: {
        type: "category",
        data: timeBuckets,
        axisLabel: {
          color: "#6b7280",
          fontSize: 11,
          rotate: timeBuckets.length > 12 ? 45 : timeBuckets.length > 6 ? 25 : 0,
          formatter: (v: string) => formatTimeBucket(v, query.timeDimension!.granularity),
        },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: "#6b7280", fontSize: 11, formatter: (v: number) => formatValue(v, stackTooltipFmt) },
        splitLine: { lineStyle: { color: "rgba(17,24,39,0.06)" } },
      },
      series: seriesByGroup,
    } as ChartOption;
  }

  // For horizontal bar charts the CATEGORY lives on the y-axis (i.e. the
  // dimension), not the x-axis.
  const categoryKey = horizontal
    ? (dimension ?? viz.encodings.y ?? yKey)
    : (dimension ?? viz.encodings.x ?? xKey);

  // Roll up rows that share the same category. Happens when the query
  // pulls a dimension AND a timeDimension but the chart shape only wants
  // one bar per category (the classic "X by recruiter, ranked" case).
  // Sum integer-ish measures; average percent-formatted ones.
  const rows = aggregateByCategory(
    data.rows,
    categoryKey,
    query.measures,
    data.columns,
    viz.type === "stacked_bar",
  );

  // Sort so the highest value reads first.
  //   vertical bar:   x-axis array[0] is the LEFT-MOST column     → sort desc (highest left)
  //   horizontal_bar: y-axis array[0] is the BOTTOM-MOST row      → sort asc  (highest at top)
  // Skip for stacked_bar so stacks line up across the dimension's natural order.
  if (viz.type !== "stacked_bar") {
    rows.sort((a, b) => {
      const da = Number(a[measure] ?? 0);
      const db = Number(b[measure] ?? 0);
      return horizontal ? da - db : db - da;
    });
  }
  const sortedData = { ...data, rows };
  // For up to ~15 categories, force every label to show (ECharts default
  // decimates which hides every other recruiter name in our ranked bars).
  // Past 15, let ECharts decide so we don't smash labels into one another.
  const forceAllLabels = sortedData.rows.length <= 15;
  const categoryAxis = {
    type: "category" as const,
    data: sortedData.rows.map((r) => r[categoryKey] as string),
    axisLabel: {
      color: "#6b7280",
      fontSize: 11,
      // Force every label up to ~15 categories; past 20 let ECharts
      // pick the auto interval to drop every Nth label.
      interval: forceAllLabels ? 0 : sortedData.rows.length > 20 ? "auto" : 0,
      // Responsive rotation: 6-12 categories rotates 25°, 13+
      // rotates 45° (steeper because labels are now squeezed).
      rotate: horizontal
        ? 0
        : sortedData.rows.length > 12
          ? 45
          : sortedData.rows.length > 6
            ? 25
            : 0,
      // Truncate long category names. Vertical bars rotated 25° look
      // janky past ~14 chars; horizontal bars have more room (~24).
      // Also strip a leading "Cube.field-" prefix that leaks when
      // dim values carry the field path (see sankey/heatmap fixes).
      formatter: (v: string) => {
        if (typeof v !== "string") return v;
        // Time-bucketed category axis (e.g. a bar of opens by month): format the
        // ISO bucket like the line/stacked paths rather than show "2026-04-01".
        if (categoryKey === query.timeDimension?.member) {
          return formatTimeBucket(v, query.timeDimension?.granularity);
        }
        const cleaned = v.replace(/^[A-Z][A-Za-z]+\.[a-zA-Z]+[-_]/, "");
        const cap = horizontal ? 24 : 14;
        return cleaned.length > cap ? cleaned.slice(0, cap - 1) + "…" : cleaned;
      },
    },
  };
  // Integer-count nudge for small-range bars too.
  const barValues = sortedData.rows.flatMap((r) => query.measures.map((m) => Number(r[m] ?? 0)));
  const barMax = barValues.length > 0 ? Math.max(...barValues) : 0;
  const barUseIntTicks = (yFmt === "number" || !yFmt) && barMax > 0 && barMax < 10;
  const valueAxis = {
    type: "value" as const,
    axisLabel: {
      color: "#6b7280",
      fontSize: 11,
      formatter: (v: number) => formatValue(v, yFmt),
    },
    splitLine: { lineStyle: { color: "rgba(17,24,39,0.06)" } },
    ...(barUseIntTicks ? { minInterval: 1 } : {}),
  };
  return {
    ...base,
    tooltip: {
      trigger: "axis",
      valueFormatter: (v: unknown) => formatValue(v, yFmt),
    },
    legend: {
      // Show whenever there's > 1 measure OR a comparison overlay
      // exists; otherwise the dashed "(prev)" series goes anonymous.
      show: query.measures.length > 1 || (data.comparisonRows && data.comparisonRows.length > 0),
      bottom: 0,
      textStyle: { color: "#6b7280", fontSize: 11 },
      // Truncate long measure names so they don't push the legend
      // off-screen when there are 4+ measures.
      formatter: (n: string) => (n.length > 22 ? n.slice(0, 21) + "…" : n),
    },
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxis,
    series: [
      ...query.measures.map((m, idx) => {
        const mFmt = formatFromColumn(m, data.columns);
        const isStacked = viz.type === "stacked_bar";
        const isOutermost = idx === query.measures.length - 1;
        return {
          name: humanizeMeasure(m),
          type: "bar" as const,
          stack: isStacked ? "total" : undefined,
          itemStyle: { borderRadius: horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0] },
          data: sortedData.rows.map((r) => {
            const v = Number(r[m] ?? 0);
            // For stacked bars, hide labels on segments < 8% of the
            // stack height — otherwise tiny segments stack their
            // labels in a column that overlaps unreadable.
            if (isStacked && !isOutermost) {
              const total = query.measures.reduce(
                (acc, mm) => acc + Number(r[mm] ?? 0),
                0,
              );
              const share = total > 0 ? v / total : 0;
              return {
                value: v,
                label: {
                  show: share >= 0.08,
                  position: "inside" as const,
                  fontSize: 10,
                  color: "#fff",
                  formatter: () => formatValue(v, mFmt),
                },
              };
            }
            return v;
          }),
          color: palette[idx % palette.length],
          // Outermost segment of a stacked bar carries the TOTAL
          // (sum across all measures for that row) so the user can
          // read the stack height in one glance.
          label: {
            show: !isStacked || isOutermost,
            position: horizontal ? "right" : "top",
            fontSize: 10,
            color: "#4b5563",
            distance: 6,
            formatter: (p: { value: number; dataIndex: number }) => {
              if (isStacked && isOutermost) {
                const row = sortedData.rows[p.dataIndex];
                if (row) {
                  const total = query.measures.reduce(
                    (acc, mm) => acc + Number(row[mm] ?? 0),
                    0,
                  );
                  return formatValue(total, mFmt);
                }
              }
              return formatValue(p.value, mFmt);
            },
          },
        };
      }),
      // ── Comparison-period overlay for non-stacked bar charts. We
      // align by category — for any category present in the primary
      // rows, pull the matching previous-period value (or 0 if the
      // recruiter / client / channel didn't exist last period). The
      // bars share an x position via barGap=-100% so they sit
      // behind/inside the current bars rather than alongside, which
      // would compress the chart at typical dashboard widths.
      ...(viz.type !== "stacked_bar" && data.comparisonRows && data.comparisonRows.length > 0
        ? (() => {
            const cmpByCat = new Map<string, Record<string, number>>();
            for (const r of data.comparisonRows ?? []) {
              const key = String(r[categoryKey] ?? "");
              const acc = cmpByCat.get(key) ?? {};
              for (const m of query.measures) {
                acc[m] = (acc[m] ?? 0) + Number(r[m] ?? 0);
              }
              cmpByCat.set(key, acc);
            }
            return query.measures.map((m, idx) => ({
              name: `${humanizeMeasure(m)} (prev)`,
              type: "bar" as const,
              data: sortedData.rows.map((r) => cmpByCat.get(String(r[categoryKey]))?.[m] ?? 0),
              itemStyle: {
                color: palette[idx % palette.length],
                opacity: 0.32,
                borderRadius: horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0],
              },
              barGap: "-100%",
              z: 1,
              label: { show: false },
            }));
          })()
        : []),
    ],
  } as ChartOption;
}
